import { useState, useEffect } from 'react';
import { Plane, FileText, LogOut, Bell, User, Settings, TestTube, X } from 'lucide-react';
import { isFeatureEnabled, updateFeature } from '../config/features';
import VersionDisplay from './VersionDisplay';

function Sidebar({ currentPage, setCurrentPage, onSignOut, userAttributes, isOpen, onClose, isMobile }) {
  const [flightTrackingEnabled, setFlightTrackingEnabled] = useState(isFeatureEnabled('FLIGHT_TRACKING_ENABLED'));
  const [showSettings, setShowSettings] = useState(false);

  // Listen for feature toggle changes
  useEffect(() => {
    const handleFeatureToggle = (event) => {
      if (event.detail.featureName === 'FLIGHT_TRACKING_ENABLED') {
        setFlightTrackingEnabled(event.detail.enabled);
      }
    };

    window.addEventListener('featureToggleChanged', handleFeatureToggle);
    
    return () => {
      window.removeEventListener('featureToggleChanged', handleFeatureToggle);
    };
  }, []);

  const toggleFlightTracking = () => {
    const newValue = !flightTrackingEnabled;
    setFlightTrackingEnabled(newValue);
    updateFeature('FLIGHT_TRACKING_ENABLED', newValue);
    
    // Show a message to the user
    if (newValue) {
      alert('Flight tracking enabled! Real-time flight data will now be fetched.');
    } else {
      alert('Flight tracking disabled. This will save API costs but flight data will not be updated.');
    }
  };

  // Check if user is admin for flight testing
  const userEmail = userAttributes?.email || '';
  const isAdmin = userEmail === 'dmilliorn+tf1@gmail.com' || userEmail === 'dmilliorn+ft1@gmail.com';

  const menuItems = [
    {
      id: 'trips',
      label: 'My Trips',
      icon: Plane,
      color: '#dc2626'
    },
    {
      id: 'create',
      label: 'Create Trip',
      icon: FileText,
      color: '#1e40af'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      color: '#059669'
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: User,
      color: '#7c3aed'
    },
    // Admin only: Flight Testing
    ...(isAdmin ? [{
      id: 'flightTest',
      label: 'Flight Testing',
      icon: TestTube,
      color: '#ea580c'
    }] : [])
  ];

  const handleMenuItemClick = (pageId) => {
    setCurrentPage(pageId);
    if (onClose) {
      onClose(); // Close sidebar on mobile after selection
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && isMobile && (
        <div 
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999
          }}
        />
      )}

      {/* Sidebar */}
      <div style={{
        width: '250px',
        background: 'white',
        boxShadow: '2px 0 4px -1px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        position: 'fixed',
        left: (isOpen || !isMobile) ? 0 : '-250px',
        top: 0,
        zIndex: 1000,
        transition: 'left 0.3s ease-in-out'
      }}>
      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#1f2937',
            margin: '0 0 4px 0'
          }}>
            TravelFlow
          </h1>
          <p style={{
            fontSize: '12px',
            color: '#6b7280',
            margin: '0'
          }}>
            Welcome, {(() => {
            if (userAttributes?.given_name && userAttributes?.family_name) {
              return `${userAttributes.given_name} ${userAttributes.family_name}`;
            }
            if (userAttributes?.firstName && userAttributes?.lastName) {
              return `${userAttributes.firstName} ${userAttributes.lastName}`;
            }
            if (userAttributes?.name) {
              return userAttributes.name;
            }
            if (userAttributes?.email) {
              const emailName = userAttributes.email.split('@')[0].replace(/[._+]/g, ' ').replace(/\d+/g, '').trim();
              if (emailName && emailName !== '') {
                return emailName;
              }
            }
            return 'User';
          })()}
        </p>
        </div>
        
        {/* Mobile Close Button */}
        {onClose && isMobile && (
          <button
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#6b7280',
              borderRadius: '6px',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Navigation Menu */}
      <nav style={{
        flex: 1,
        padding: '20px 0'
      }}>
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentPage === item.id;
          
          return (
            <button
              key={item.id}
              onClick={() => handleMenuItemClick(item.id)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 20px',
                background: isActive ? '#f3f4f6' : 'transparent',
                border: 'none',
                color: isActive ? item.color : '#6b7280',
                fontSize: '14px',
                fontWeight: isActive ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderRight: isActive ? `3px solid ${item.color}` : '3px solid transparent'
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.target.style.background = '#f9fafb';
                  e.target.style.color = '#374151';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#6b7280';
                }
              }}
            >
              <Icon size={18} />
              {item.label}
            </button>
          );
        })}
      </nav>

      {/* Donation Section — commented out for now, kept for future re-enablement
      <div style={{
        padding: '20px',
        borderTop: '1px solid #e5e7eb',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          borderRadius: '12px',
          padding: '16px',
          textAlign: 'center',
          border: '1px solid #f59e0b'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '8px'
          }}>
            <Heart size={16} color="#f59e0b" />
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#92400e'
            }}>
              Support Development
            </span>
          </div>
          
          <p style={{
            fontSize: '12px',
            color: '#92400e',
            margin: '0 0 12px 0',
            lineHeight: '1.4'
          }}>
            Love TravelFlow? Help keep it free and improve it!<br />
            <span style={{ fontSize: '11px', opacity: 0.8 }}>Venmo: @Dan-Milliorn • Zelle: dmilliorn@gmail.com</span>
          </p>
          
          <div style={{
            display: 'flex',
            gap: '8px',
            justifyContent: 'center'
          }}>
            <button
              onClick={() => {
                // Open Venmo with Dan's account
                const venmoUrl = 'https://account.venmo.com/u/Dan-Milliorn';
                window.open(venmoUrl, '_blank', 'noopener,noreferrer');
              }}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '8px 12px',
                background: '#3d5a80',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#2d4a6b'}
              onMouseLeave={(e) => e.target.style.background = '#3d5a80'}
              title="Send support via Venmo to @Dan-Milliorn"
            >
              <CreditCard size={14} />
              Venmo
            </button>
            
            <button
              onClick={() => {
                const zelleEmail = 'dmilliorn@gmail.com';
                navigator.clipboard.writeText(zelleEmail).then(() => {
                  alert(`Zelle email copied to clipboard: ${zelleEmail}\n\nInstructions:\n1. Open your banking app\n2. Find "Send Money with Zelle"\n3. Enter: ${zelleEmail}\n4. Send your support!`);
                }).catch(() => {
                  alert(`Zelle Email: ${zelleEmail}\n\nInstructions:\n1. Open your banking app\n2. Find "Send Money with Zelle"\n3. Enter: ${zelleEmail}\n4. Send your support!`);
                });
              }}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '8px 12px',
                background: '#6b46c1',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#553c9a'}
              onMouseLeave={(e) => e.target.style.background = '#6b46c1'}
              title="Copy Zelle email: dmilliorn@gmail.com"
            >
              <CreditCard size={14} />
              Zelle
            </button>
          </div>

          
          <p style={{
            fontSize: '10px',
            color: '#a16207',
            margin: '8px 0 0 0',
            fontStyle: 'italic'
          }}>
            Every contribution helps! 💙
          </p>
        </div>
      </div>
      */}

      {/* Settings Section */}
      <div style={{
        padding: '16px 20px',
        borderTop: '1px solid #e5e7eb'
      }}>
        <button
          onClick={() => setShowSettings(!showSettings)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 16px',
            background: 'transparent',
            color: '#6b7280',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#f3f4f6';
            e.target.style.color = '#374151';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
            e.target.style.color = '#6b7280';
          }}
        >
          <Settings size={16} />
          Settings
        </button>

        {showSettings && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '8px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Plane size={14} color="#6b7280" />
                <span style={{ fontSize: '12px', color: '#374151', fontWeight: '500' }}>
                  Flight Tracking
                </span>
              </div>
              <button
                onClick={toggleFlightTracking}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: flightTrackingEnabled ? '#059669' : '#6b7280'
                }}
                title={flightTrackingEnabled ? 'Disable flight tracking to save costs' : 'Enable real-time flight tracking'}
              >
                {flightTrackingEnabled ? (
                  <div style={{ fontSize: '20px' }}>●</div>
                ) : (
                  <div style={{ fontSize: '20px', opacity: 0.3 }}>○</div>
                )}
              </button>
            </div>
            <p style={{
              fontSize: '10px',
              color: '#6b7280',
              margin: 0,
              lineHeight: '1.4'
            }}>
              {flightTrackingEnabled 
                ? 'Real-time flight data enabled (uses API calls)' 
                : 'Flight tracking disabled to save API costs'
              }
            </p>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div style={{
        padding: '20px',
        borderTop: '1px solid #e5e7eb'
      }}>
        <button
          onClick={onSignOut}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 16px',
            background: 'transparent',
            color: '#6b7280',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#f3f4f6';
            e.target.style.color = '#374151';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
            e.target.style.color = '#6b7280';
          }}
        >
          <LogOut size={16} />
          Sign Out
        </button>
      </div>

      <VersionDisplay />

    </div>
    </>
  );
}

export default Sidebar;
